import React, { useState, useEffect, useRef } from "react";
import { confirm } from "../utils/confirm";
import RicordamiAlle from "./RicordamiAlle";
import { messaggioErrore } from "../utils/erroriUtente";
import { X, Plus, Trash2, CheckCircle, Circle, ChevronDown, ChevronRight, User, Calendar, Flag, Tag, AlignLeft, LayoutGrid } from "lucide-react";
import { doc, updateDoc, deleteDoc, addDoc, collection, onSnapshot, query, where, serverTimestamp, getDocs, getDoc, deleteField, arrayUnion, arrayRemove } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { Triggers } from "../utils/ruleEngine";
import { isTaskOverdue } from "../utils/taskHelpers";
import { addDays, addMonths, addYears } from "date-fns";
import { toLocalDateStr } from "../utils/dateUtils";
import { linkMappa, linkTelefono } from "../utils/sediCliente";
import { ruoloSuAttivita, utentiCondivisibili, differenzaPersone, puoGestireSottoattivita } from "../utils/condivisione";
import { avvisaCambioStato, avvisaSeguaci } from "../utils/avvisiAttivita";
import { spuntaSottoattivita } from "../utils/sottoattivitaElenco";
import { campiCompletamento, avvisoCompletamentoDiAltri, testoCompletataDa } from "../utils/completamentoAttivita";
import DescrizioneAttivita from "./DescrizioneAttivita";
import CommentiAttivita from "./CommentiAttivita";
import { testoSemplice } from "../utils/testoFormattato";

// Checkbox circolare
function CheckCircleBtn({ checked, onChange, size=18 }) {
  return (
    <div className={`check-circle ${checked?"checked":""}`} style={{width:size,height:size}} onClick={e=>{e.stopPropagation();onChange(!checked);}}>
      {checked && <svg width={size*0.6} height={size*0.6} viewBox="0 0 10 10"><polyline points="1,5 4,8 9,2" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round"/></svg>}
    </div>
  );
}

// Commenti con foto e file, modificabili con storico: components/CommentiAttivita.js (W440)

// Sottoattività
function SubtasksSection({ taskId, taskTitle, organizationId, projectId=null, onSelectSubtask, selectedSubtaskId, commessaChiusa=false, ruoloAttivita="pieno" }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const [subtasks, setSubtasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title:"", dueDate:"", assignedTo:"", assignedName:"", priority:"media" });
  const [loading, setLoading] = useState(false);
  const titleRef = useRef();

  useEffect(() => {
    const q = query(collection(db,"subtasks"), where("parentTaskId","==",taskId), where("organizationId","==",organizationId));
    const unsub = onSnapshot(q, snap => {
      const list = snap.docs.map(d=>({id:d.id,...d.data()}));
      list.sort((a,b)=>(a.createdAt?.toMillis?.()|| 0)-(b.createdAt?.toMillis?.()|| 0));
      setSubtasks(list);
    });
    // Stesso bug corretto poco sopra in AssigneeField: mancava il filtro per
    // organizzazione, la lista arrivava sempre vuota per chiunque non fosse Platform Owner.
    getDocs(query(collection(db,"users"),where("organizationId","==",organizationId),where("enabled","==",true))).then(s=>setUsers(s.docs.map(d=>d.data())));
    return unsub;
  }, [taskId, organizationId]);

  useEffect(() => { if (adding && titleRef.current) titleRef.current.focus(); }, [adding]);

  const openAdd = () => {
    setForm({ title: taskTitle + " - ", dueDate:"", assignedTo:"", assignedName:"", priority:"media" });
    setAdding(true);
  };

  const save = async e => {
    e.preventDefault();
    if (commessaChiusa || !form.title.trim()) return;
    setLoading(true);
    await addDoc(collection(db,"subtasks"), { ...form, parentTaskId:taskId, parentTaskTitle:taskTitle, organizationId:organizationId||null, status:"aperta", reminderEnabled:false, reminderDays:1, emailOnDue:true, emailOnReminder:true, emailOnOverdue:true, createdBy:currentUser.uid, createdAt:serverTimestamp() });
    setAdding(false); setLoading(false);
  };

  // Spunta: chi ha pieni diritti sull'attività, chi ha creato la sottoattività o ne è
  // incaricato (utils/completamentoAttivita.js). Avviso a chi la segue; messaggio viola se
  // si completa la sottoattività di altri.
  const toggle = async st => {
    if (commessaChiusa) return;
    try {
      await spuntaSottoattivita(st, { ruoloAttivita, utente: currentUser, projectId, addToast });
    } catch (err) { addToast(messaggioErrore(err, "aggiornare la sottoattività"), "error"); }
  };
  const del = async st => {
    if (commessaChiusa || !puoGestireSottoattivita(st, ruoloAttivita, currentUser?.uid)) return;
    if (!await confirm(`Eliminare la sottoattività "${st.title}"?`, { type:"danger" })) return;
    await deleteDoc(doc(db,"subtasks",st.id));
  };

  const done = subtasks.filter(s=>s.status==="completata").length;

  return (
    <div className="detail-section">
      <div className="detail-section-title" style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <span>Sottoattività {subtasks.length>0 && `(${done}/${subtasks.length})`}</span>
        {!commessaChiusa && <button className="btn btn-ghost btn-sm" onClick={openAdd} style={{fontSize:12}}><Plus size={12}/> Aggiungi</button>}
      </div>

      {subtasks.length>0 && (
        <div className="progress-bar" style={{marginBottom:10}}>
          <div className="progress-bar-fill" style={{width:`${subtasks.length?Math.round(done/subtasks.length*100):0}%`}}/>
        </div>
      )}

      {subtasks.map(st => {
        const stOverdue = isTaskOverdue(st);
        return (
        <div key={st.id} className="subtask-row"
          style={{cursor:"pointer", background: selectedSubtaskId===st.id ? "#faf5ff" : "transparent", borderRadius:6, padding:"4px 6px"}}
          onClick={()=>onSelectSubtask && onSelectSubtask(st)}>
          <div onClick={e=>e.stopPropagation()}>
            <CheckCircleBtn checked={st.status==="completata"} onChange={()=>{ if(!commessaChiusa) toggle(st); }}/>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontSize:13,textDecoration:st.status==="completata"?"line-through":"none",color:st.status==="completata"?"#9ca3af":stOverdue?"#dc2626":"#111827"}}>{st.title}</span>
              {stOverdue && <span style={{fontSize:10,color:"#dc2626",fontWeight:600,background:"#fee2e2",padding:"1px 5px",borderRadius:4,flexShrink:0}}>SCADUTA</span>}
            </div>
            {(st.dueDate||st.assignedName) && (
              <div style={{display:"flex",gap:8,marginTop:2}}>
                {st.dueDate && <span style={{fontSize:11,color:stOverdue?"#dc2626":"#9ca3af"}}>📅 {st.dueDate.split("-").reverse().join("/")}{st.dueTime?" "+st.dueTime:""}</span>}
                {st.assignedName && <span style={{fontSize:11,color:"#9ca3af"}}>👤 {st.assignedName}</span>}
              </div>
            )}
            {st.description && <div style={{fontSize:11,color:"#9ca3af",marginTop:2,fontStyle:"italic",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{testoSemplice(st.description)}</div>}
          </div>
          <div className={`priority-dot ${st.priority}`}/>
          {!commessaChiusa && puoGestireSottoattivita(st, ruoloAttivita, currentUser?.uid) && <button className="btn-icon" title="Elimina sottoattività" onClick={e=>{e.stopPropagation();del(st);}}><Trash2 size={12}/></button>}
        </div>
        );
      })}

      {adding && !commessaChiusa && (
        <div style={{background:"#f6f8fa",borderRadius:8,border:"1px solid #7c3aed",padding:12,marginTop:8}}>
          <form onSubmit={save}>
            <input ref={titleRef} className="input" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} placeholder="Titolo sottoattività..." style={{marginBottom:8}} required/>
            <textarea className="input" value={form.description||""} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="Descrizione sottoattività (opzionale)..." rows={2} style={{marginBottom:8,fontSize:13,resize:"vertical"}}/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
              <div style={{display:"flex",gap:4}}>
                <input className="input" type="date" value={form.dueDate} onChange={e=>setForm(f=>({...f,dueDate:e.target.value}))} style={{flex:1}}/>
                <input className="input" type="time" value={form.dueTime||""} onChange={e=>setForm(f=>({...f,dueTime:e.target.value}))} style={{width:90}}/>
              </div>
              <select className="input" value={form.priority} onChange={e=>setForm(f=>({...f,priority:e.target.value}))}>
                <option value="alta">Alta</option>
                <option value="media">Media</option>
                <option value="bassa">Bassa</option>
              </select>
              <select className="input" value={form.assignedTo} onChange={e=>{
                const u=users.find(u=>u.uid===e.target.value);
                setForm(f=>({...f,assignedTo:e.target.value,assignedName:u?.displayName||""}));
              }}>
                <option value="">Nessuno</option>
                {users.map(u=><option key={u.uid} value={u.uid}>{u.displayName}</option>)}
              </select>
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={()=>setAdding(false)}>Annulla</button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>{loading?"...":"Aggiungi"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// Campi dati
function CustomFieldsSection({ task, sectionId, commessaChiusa=false }) {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState(task.customFields||{});
  const [clienteData, setClienteData] = useState(null);
  const { addToast } = useToast();

  // Ricarica valori quando cambia task
  useEffect(() => { setValues(task.customFields||{}); }, [task.id]);

  // Carica dati cliente se la commessa ha un cliente collegato
  useEffect(() => {
    if (!task.projectId) return;
    getDoc(doc(db,"projects",task.projectId)).then(snap => {
      if (!snap.exists() || !snap.data().clienteId) return;
      getDoc(doc(db,"clienti",snap.data().clienteId)).then(cSnap => {
        if (cSnap.exists()) setClienteData(cSnap.data());
      });
    });
  }, [task.projectId]);

  useEffect(() => {
    if (!sectionId) return;
    const q = query(collection(db,"customFields"), where("sectionId","==",sectionId));
    return onSnapshot(q, snap => {
      const list = snap.docs.map(d=>({id:d.id,...d.data()}));
      // ordine: campi creati insieme (es. modelli importati); gli altri seguono per data di creazione
      list.sort((a,b)=>((a.ordine??1000)-(b.ordine??1000)) || ((a.createdAt?.toMillis?.()||0)-(b.createdAt?.toMillis?.()||0)));
      setFields(list);
    });
  }, [sectionId]);

  if (!sectionId || fields.length===0) return null;

  // Valore effettivo: se il campo è collegato al cliente, usa il valore del cliente
  const getEffectiveValue = (field) => {
    if (field.linkedClienteField && clienteData?.[field.linkedClienteField]) {
      return { value: clienteData[field.linkedClienteField], isLinked: true };
    }
    return { value: values[field.id]||"", isLinked: false };
  };

  const saveField = async (fieldId, value) => {
    if (commessaChiusa) return;
    const newValues = { ...values, [fieldId]: value };
    setValues(newValues);
    await updateDoc(doc(db,"tasks",task.id), { customFields: newValues, updatedAt: serverTimestamp() });
  };

  return (
    <div className="detail-section">
      <div className="detail-section-title">📋 Campi dati</div>
      {fields.map(f => {
        const { value: effVal, isLinked } = getEffectiveValue(f);
        return (
          <div key={f.id} style={{display:"flex",alignItems:"center",gap:12,marginBottom:10,minHeight:36}}>
            <div style={{width:130,flexShrink:0,fontSize:12,fontWeight:600,color:"#374151",display:"flex",alignItems:"center",gap:4}}>
              {f.name}{f.required&&<span style={{color:"#dc2626"}}> *</span>}
              {isLinked && <span title="Collegato al cliente" style={{fontSize:10,color:"#2563eb"}}>🔗</span>}
            </div>
            <div style={{flex:1}}>
              {isLinked ? (
                <div style={{padding:"6px 10px",background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:6,fontSize:13,color:"#1e40af",fontWeight:500}}>{effVal}</div>
              ) : f.type==="checkbox" ? (
                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                  <input type="checkbox" checked={!!values[f.id]} onChange={e=>saveField(f.id,e.target.checked)} style={{accentColor:"#7c3aed",width:16,height:16}}/>
                  <span style={{fontSize:13,color:"#374151"}}>Sì</span>
                </label>
              ) : f.type==="tendina" ? (
                <select className="input" value={values[f.id]||""} onChange={e=>saveField(f.id,e.target.value)} style={{width:"100%"}}>
                  <option value="">Seleziona...</option>
                  {(f.options||[]).map(o=><option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type==="data" ? (
                <input className="input" type="date" value={values[f.id]||""} onChange={e=>saveField(f.id,e.target.value)} style={{width:"100%"}}/>
              ) : f.type==="valuta" ? (
                <div style={{position:"relative"}}>
                  <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"#6b7280",fontWeight:600}}>€</span>
                  <input className="input" type="number" value={values[f.id]||""} onChange={e=>saveField(f.id,e.target.value)} onBlur={e=>saveField(f.id,e.target.value)} step="0.01" style={{paddingLeft:24,width:"100%"}}/>
                </div>
              ) : (
                <input className="input" type={f.type==="numero"?"number":"text"} value={values[f.id]||""} onChange={e=>setValues(v=>({...v,[f.id]:e.target.value}))} onBlur={e=>saveField(f.id,e.target.value)} placeholder={f.placeholder||""} style={{width:"100%"}}/>
              )}
              {/* Campi di tipo indirizzo → mappa, telefono → chiamata */}
              {f.formato==="indirizzo" && linkMappa(effVal) && (
                <a href={linkMappa(effVal)} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:"#2563eb",fontWeight:600,display:"inline-block",marginTop:3}}>📍 Apri in Maps</a>
              )}
              {f.formato==="telefono" && linkTelefono(effVal) && (
                <a href={linkTelefono(effVal)} style={{fontSize:11,color:"#2563eb",fontWeight:600,display:"inline-block",marginTop:3}}>📞 Chiama</a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ── StatoTaskButtons — componente separato per gestione stati task ───────────
const STATI_TASK = {
  aperta:     { label:"aperta",     bg:"#ede9fe", col:"#7c3aed", border:"#c4b5fd" },
  in_corso:   { label:"in corso",   bg:"#dbeafe", col:"#2563eb", border:"#93c5fd" },
  completata: { label:"completata", bg:"#d1fae5", col:"#059669", border:"#6ee7b7" },
  sospesa:    { label:"sospesa",    bg:"#fef3c7", col:"#d97706", border:"#fcd34d" },
  annullata:  { label:"annullata",  bg:"#fee2e2", col:"#dc2626", border:"#fca5a5" },
};

function StatoTaskButtons({ task, currentUser, taskId, commessaChiusa=false }) {
  const { addToast } = useToast();
  // Stato locale per aggiornamento immediato UI prima che arrivi Firestore
  const [statoUI, setStatoUI] = useState(task.status || "aperta");

  // Sincronizza con il task aggiornato da Firestore
  useEffect(() => {
    setStatoUI(task.status || "aperta");
  }, [task.status]);

  const cambiaStato = async (nuovoStato) => {
    if (commessaChiusa || nuovoStato === statoUI) return;
    setStatoUI(nuovoStato); // aggiorna UI immediatamente
    avvisaCambioStato({ ...task, id: taskId, status: statoUI }, nuovoStato, currentUser);
    const ora = new Date().toISOString();
    const aggiornamento = { status: nuovoStato, updatedAt: serverTimestamp() };
    if (nuovoStato === "completata" || statoUI === "completata") Object.assign(aggiornamento, campiCompletamento(nuovoStato, currentUser));
    const viola = nuovoStato === "completata" ? avvisoCompletamentoDiAltri(task, currentUser?.uid) : null;
    if (viola) addToast(viola, "viola", 8000);

    if (nuovoStato === "sospesa" || nuovoStato === "annullata") {
      aggiornamento.dueDatePrecedente = task.dueDate || null;
      aggiornamento.dueTimePrecedente = task.dueTime || null;
      aggiornamento[`${nuovoStato}Da`] = currentUser?.displayName || "";
      aggiornamento[`${nuovoStato}DaUid`] = currentUser?.uid || "";
      aggiornamento[`${nuovoStato}Il`] = ora;
      // Due chiamate separate — deleteField non funziona mescolato con assegnazioni
      await updateDoc(doc(db, "tasks", taskId), aggiornamento);
      await updateDoc(doc(db, "tasks", taskId), {
        dueDate: deleteField(),
        dueTime: deleteField(),
      });
      return;
    }

    if ((task.status === "sospesa" || task.status === "annullata") &&
        (nuovoStato === "aperta" || nuovoStato === "in_corso")) {
      if (task.dueDatePrecedente) {
        aggiornamento.dueDate = task.dueDatePrecedente;
        aggiornamento.dueTime = task.dueTimePrecedente || "";
        aggiornamento.dueDatePrecedente = deleteField();
        aggiornamento.dueTimePrecedente = deleteField();
      }
    }

    await updateDoc(doc(db, "tasks", taskId), aggiornamento);
  };

  const statoCorrente = statoUI;

  return (
    <div style={{marginBottom:12}}>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:
        (statoCorrente==="sospesa"||statoCorrente==="annullata")?6:16}}>
        {Object.entries(STATI_TASK).map(([k,v])=>{
          const attivo = statoCorrente === k;
          return (
            <button key={k} onClick={()=>cambiaStato(k)} disabled={commessaChiusa} style={{
              padding:"3px 10px", borderRadius:20,
              border:`1px solid ${attivo ? v.border : "#e5e7eb"}`,
              background: attivo ? v.bg : "transparent",
              color: attivo ? v.col : "#6b7280",
              fontSize:12, cursor: commessaChiusa ? "default" : "pointer",
              fontFamily:"'Inter',sans-serif",
              fontWeight: attivo ? 700 : 500,
              opacity: commessaChiusa && !attivo ? 0.5 : 1,
              transition:"all 0.15s"
            }}>{v.label}</button>
          );
        })}
      </div>
      {statoCorrente === "annullata" && task.annullataDa && (
        <div style={{fontSize:11,color:"#dc2626",background:"#fef2f2",
          border:"1px solid #fca5a5",borderRadius:6,padding:"5px 10px",marginBottom:8}}>
          🚫 Annullata da <strong>{task.annullataDa}</strong>
          {task.annullataIl && ` · ${new Date(task.annullataIl).toLocaleDateString("it-IT")}`}
          {task.dueDatePrecedente && (
            <span style={{color:"#9ca3af"}}> · Scadenza era: {task.dueDatePrecedente}</span>
          )}
        </div>
      )}
      {statoCorrente === "sospesa" && task.sospesaDa && (
        <div style={{fontSize:11,color:"#d97706",background:"#fffbeb",
          border:"1px solid #fde68a",borderRadius:6,padding:"5px 10px",marginBottom:8}}>
          ⏸ Sospesa da <strong>{task.sospesaDa}</strong>
          {task.sospesaIl && ` · ${new Date(task.sospesaIl).toLocaleDateString("it-IT")}`}
          {task.dueDatePrecedente && (
            <span style={{color:"#9ca3af"}}> · Scadenza era: {task.dueDatePrecedente}</span>
          )}
        </div>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

export default function TaskDetailPanel({ task: taskProp, projectName, onClose, onUpdate, onSelectSubtask, selectedSubtaskId }) {
  const { currentUser, userProfile, isOrgAdmin, isPlatformOwner } = useAuth();
  const { addToast } = useToast();
  const isAdmin = userProfile?.role==="admin" || userProfile?.role==="titolare" || !!isOrgAdmin || !!isPlatformOwner;
  const [editing, setEditing] = useState({});
  const [panelWidth, setPanelWidth] = useState(() => Math.min(640, window.innerWidth * 0.5));
  const resizingRef = useRef(false);
  const [taskLive, setTaskLive] = useState(taskProp);

  // Listener realtime sul task — aggiorna il pannello quando Firestore cambia
  useEffect(() => {
    if (!taskProp?.id) return;
    setTaskLive(taskProp); // aggiorna subito con la prop
    const unsub = onSnapshot(doc(db, "tasks", taskProp.id), snap => {
      if (snap.exists()) setTaskLive({ id: snap.id, ...snap.data() });
    });
    return () => unsub();
  }, [taskProp?.id]);

  const task = taskLive || taskProp;

  // Commessa chiusa = attività di sola consultazione — verificato qui (non nel chiamante) perché
  // questo pannello si apre da 3 punti diversi (dentro la commessa, "Le mie attività", vista rapida
  // Home/Notifiche): il controllo deve valere ovunque, non solo quando si apre dalla commessa.
  const [commessaChiusa, setCommessaChiusa] = useState(false);
  const [responsabileCommessaUid, setResponsabileCommessaUid] = useState("");
  const [membriCommessa, setMembriCommessa] = useState([]);
  useEffect(() => {
    if (!task?.projectId) { setCommessaChiusa(false); setResponsabileCommessaUid(""); setMembriCommessa([]); return; }
    return onSnapshot(doc(db, "projects", task.projectId), snap => {
      setCommessaChiusa(snap.exists() && snap.data().statoCommessa === "chiusa");
      setResponsabileCommessaUid(snap.exists() ? (snap.data().responsabileUid || "") : "");
      setMembriCommessa(snap.exists() && Array.isArray(snap.data().membri) ? snap.data().membri : []);
    }, () => {});
  }, [task?.projectId]);
  // Avviso di cambio scadenza: si manda una volta sola, quando si smette di modificare la data
  // (il campo data cambia a ogni cifra digitata).
  const avvisoScadenza = useRef({ timer: null, prima: undefined });

  // Suggerimento discreto per i Controlli PM — solo quando l'assegnazione cambia davvero e va
  // a qualcun altro (delegare a se stessi non ha lo stesso senso di "ricordami di verificare").
  // NOTA: spostati qui, PRIMA del "if (!task) return null" qui sotto — erano dichiarati dopo,
  // violando la regola di React per cui gli hook vanno sempre chiamati, mai saltati quando task
  // è momentaneamente vuoto (bug preesistente, corretto durante l'audit query del 26/8/2026).
  const [suggerimentoControllo, setSuggerimentoControllo] = useState(null); // {assignedTo, assignedName} | null
  const [dataControlloProposta, setDataControlloProposta] = useState("");

  if (!task) return null;

  // Attività ricevuta in condivisione (utils/condivisione.js): si collabora (commenti,
  // sottoattività, nuove attività) ma nome, stato, scadenze, assegnatario, priorità,
  // descrizione ed eliminazione restano a chi ha pieni diritti.
  const ruolo = ruoloSuAttivita(task, { uid: currentUser?.uid, isAdmin, ruoloOperativo: userProfile?.ruoloOperativo || "",
    responsabileCommessaUid, membriCommessa });
  const collabora = ruolo !== "pieno";
  const bloccato = commessaChiusa || collabora;

  const update = async (field, value) => {
    if (bloccato) return;
    if (field === "status") {
      // Spunta in alto nel pannello: stesse regole dei pulsanti di stato
      avvisaCambioStato(task, value, currentUser);
      await updateDoc(doc(db,"tasks",task.id), { status:value, ...campiCompletamento(value, currentUser), updatedAt:serverTimestamp() });
      const viola = value === "completata" ? avvisoCompletamentoDiAltri(task, currentUser?.uid) : null;
      if (viola) addToast(viola, "viola", 8000);
      if (onUpdate) onUpdate({ ...task, status:value });
      return;
    }
    if (field === "dueDate" && value !== task.dueDate) {
      const a = avvisoScadenza.current;
      if (a.prima === undefined) a.prima = task.dueDate || "";
      clearTimeout(a.timer);
      const t = { ...task };
      a.timer = setTimeout(() => {
        const prima = a.prima; a.prima = undefined;
        if ((value || "") === prima || (value && !/^\d{4}-\d{2}-\d{2}$/.test(value))) return;
        avvisaSeguaci(t, currentUser, "Scadenza cambiata", `«${t.title}»: nuova scadenza ${value ? value.split("-").reverse().join("/") : "nessuna"}`);
        // Scadenza portata a OGGI da un altro: l'assegnatario riceve subito l'avviso (con
        // email e notifica, functions: emailDaNotifica) — il controllo delle 07:00 è già passato.
        if (value === toLocalDateStr(new Date()) && prima !== value && t.assignedTo && t.assignedTo !== currentUser?.uid) {
          addDoc(collection(db, "notifiche"), {
            userId: t.assignedTo, orgId: t.organizationId, tipo: "scadenza_oggi", titolo: "Scadenza anticipata a oggi",
            testo: `${currentUser?.displayName || "Un collega"} ha portato a oggi la scadenza di «${t.title || "un'attività"}»`,
            projectId: t.projectId || null, taskId: t.id, rifCollezione: "tasks", rifId: t.id, letta: false, createdAt: serverTimestamp(),
          }).catch(err => console.warn("Avviso scadenza non inviato:", err?.code || err));
        }
      }, 2500);
    }
    await updateDoc(doc(db,"tasks",task.id), { [field]:value, updatedAt:serverTimestamp() });
    if (onUpdate) onUpdate({ ...task, [field]:value });
  };

  const updateMulti = async (data) => {
    if (bloccato) return;
    const assegnazioneCambiata = "assignedTo" in data && data.assignedTo && data.assignedTo!==task.assignedTo && data.assignedTo!==currentUser?.uid;
    // chi assegna: serve al server per non avvisare chi assegna a se stesso (functions: avvisoAssegnazione)
    const autore = "assignedTo" in data ? { assegnatoDaUid: currentUser?.uid || "", assegnatoDaNome: currentUser?.displayName || "" } : {};
    await updateDoc(doc(db,"tasks",task.id), { ...data, ...autore, updatedAt:serverTimestamp() });
    if (assegnazioneCambiata) {
      const tra7gg = new Date(); tra7gg.setDate(tra7gg.getDate()+7);
      setDataControlloProposta(toLocalDateStr(tra7gg));
      setSuggerimentoControllo({ assignedTo:data.assignedTo, assignedName:data.assignedName||"" });
    }
  };

  const creaControlloDaSuggerimento = async () => {
    await addDoc(collection(db,"controlliPM"), {
      projectId: task.projectId, orgId: task.organizationId,
      titolo: `Verifica esito: ${task.title}`, nota: `Delegato a ${suggerimentoControllo.assignedName}`,
      dataVerifica: dataControlloProposta, priorita: "", stato: "Aperto",
      entityTipo: "task", entityId: task.id,
      creatoDaUid: currentUser?.uid||"", creatoDaNome: currentUser?.displayName||"",
      createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    });
    addToast("Controllo PM creato — lo trovi nella Cabina di Regia della commessa ✅","success");
    setSuggerimentoControllo(null);
  };

  const del = async () => {
    if (commessaChiusa) return;
    if (!await confirm(`Eliminare "${task.title}"?`, { type:"danger" })) return;
    await deleteDoc(doc(db,"tasks",task.id));
    addToast("Attività eliminata","info");
    onClose();
  };

  const isOverdue = isTaskOverdue(task);

  // Ridimensionamento pannello: solo restringere, max 50% finestra
  const startResize = e => {
    e.preventDefault();
    resizingRef.current = true;
    const startX = e.clientX;
    const startWidth = panelWidth;
    const maxWidth = window.innerWidth / 2;

    const onMove = me => {
      if (!resizingRef.current) return;
      const delta = startX - me.clientX;
      let newWidth = startWidth + delta;
      if (newWidth > maxWidth) newWidth = maxWidth;
      if (newWidth < 280) newWidth = 280;
      setPanelWidth(newWidth);
    };
    const onUp = () => {
      resizingRef.current = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  return (
    <div className="detail-panel" style={{ width: panelWidth, position:"relative", flexShrink:0 }}>
      {/* Maniglia ridimensionamento */}
      <div className="detail-panel-maniglia" onMouseDown={startResize}
        style={{ position:"absolute", left:0, top:0, bottom:0, width:6, cursor:"col-resize", zIndex:50 }}
        onMouseOver={e=>e.currentTarget.style.background="rgba(124,58,237,0.2)"}
        onMouseOut={e=>e.currentTarget.style.background="transparent"}/>

      {/* Panel header */}
      <div className="detail-panel-header">
        <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
          <CheckCircleBtn checked={task.status==="completata"} onChange={v=>{ if(!bloccato) update("status",v?"completata":"aperta"); }} size={20}/>
              {task.fromVerbale && (
                <span style={{fontSize:10,background:"#ede9fe",color:"#7c3aed",padding:"2px 8px",borderRadius:8,fontWeight:700,flexShrink:0}}>
                  📋 Da verbale
                </span>
              )}
          <div style={{flex:1}}>
            {projectName && (
              <div style={{fontSize:11,fontWeight:600,color:"#7c3aed",marginBottom:3,display:"flex",alignItems:"center",gap:4}}>
                📁 {projectName}
              </div>
            )}
            <div contentEditable={!bloccato} suppressContentEditableWarning
              onBlur={e=>{ const v=e.currentTarget.textContent.trim(); if(v&&v!==task.title) update("title",v); }}
              style={{fontSize:16,fontWeight:600,lineHeight:"1.4",outline:"none",minHeight:24,textDecoration:task.status==="completata"?"line-through":"none",color:task.status==="completata"?"#9ca3af":"#111827"}}>
              {task.title}
            </div>
            {isOverdue && <span style={{fontSize:11,color:"#dc2626",fontWeight:600}}>⚠ SCADUTA</span>}
            {testoCompletataDa(task) && <div style={{fontSize:11.5,color:"#059669",fontWeight:600,marginTop:2}}>✓ {testoCompletataDa(task)}</div>}
          </div>
          <div style={{display:"flex",gap:4,marginRight:40}}>
            {!bloccato && (isAdmin||task.createdBy===currentUser.uid) && (
              <button className="btn-icon" onClick={del} title="Elimina"><Trash2 size={15}/></button>
            )}
            <button className="btn-icon" onClick={onClose} aria-label="Chiudi" title="Chiudi"><X size={16}/></button>
          </div>
        </div>
        {commessaChiusa && (
          <div style={{fontSize:11,fontWeight:700,color:"#dc2626",background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:6,padding:"5px 10px",marginTop:8}}>
            🔒 Commessa chiusa — sola consultazione. Riapri la commessa per modificare questa attività.
          </div>
        )}
      </div>

      <div className="detail-panel-scroll">
        {/* Stato rapido */}
        <StatoTaskButtons task={task} currentUser={currentUser} taskId={task.id} commessaChiusa={bloccato}/>
        {collabora && !commessaChiusa && (
          <div style={{fontSize:11.5,color:"#6b7280",background:"#f9fafb",border:"1px solid #f3f4f6",borderRadius:8,padding:"6px 10px",marginBottom:10}}>
            👥 Collabori a questa attività: puoi commentare, compilare i campi dati e aggiungere sottoattività. Nome, stato, scadenze, assegnatario e priorità li cambiano {task.assignedName ? <b>{task.assignedName}</b> : "chi la segue"}, chi l'ha creata e i responsabili della commessa.
          </div>
        )}

        {/* Campi principali */}
        <div className="detail-section">
          {/* Assegnatario */}
          <AssigneeField task={task} onUpdate={updateMulti} disabled={bloccato}/>

          {/* Condivisione con colleghi della stessa azienda */}
          <CondivisioneSection task={task} puoCondividere={!collabora && !commessaChiusa} collabora={collabora} commessaChiusa={commessaChiusa}/>

          {suggerimentoControllo && (
            <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8,padding:"10px 12px",marginTop:8,marginBottom:8,fontSize:12}}>
              <div style={{color:"#5b21b6",marginBottom:6}}>
                💜 Vuoi che Nexiora ti ricordi di verificarne l'esito con <strong>{suggerimentoControllo.assignedName}</strong>?
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <input type="date" value={dataControlloProposta} onChange={e=>setDataControlloProposta(e.target.value)}
                  style={{padding:"5px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:11.5}}/>
                <button onClick={creaControlloDaSuggerimento}
                  style={{padding:"5px 12px",borderRadius:6,border:"none",background:"#7c3aed",color:"#fff",fontSize:11.5,fontWeight:700,cursor:"pointer"}}>
                  Sì, ricordamelo
                </button>
                <button onClick={()=>setSuggerimentoControllo(null)}
                  style={{padding:"5px 12px",borderRadius:6,border:"1px solid #e5e7eb",background:"#fff",color:"#6b7280",fontSize:11.5,cursor:"pointer"}}>
                  No, grazie
                </button>
              </div>
            </div>
          )}

          <fieldset disabled={bloccato} style={{border:"none",margin:0,padding:0,minWidth:0}}>
          {/* Data inizio (facoltativa) */}
          <div className="detail-field">
            <div className="detail-field-label"><Calendar size={13} style={{marginRight:4,verticalAlign:"middle"}}/>Data inizio</div>
            <input type="date" value={task.startDate||""} onChange={e=>update("startDate",e.target.value)}
              style={{border:"none",background:"transparent",fontSize:13,color:"#111827",cursor:"pointer",fontFamily:"'Inter',sans-serif",outline:"none"}}/>
            <span style={{fontSize:11,color:"#9ca3af",marginLeft:6}}>facoltativa — usata solo per il Gantt</span>
          </div>

          {/* Scadenza */}
          <div className="detail-field">
            <div className="detail-field-label"><Calendar size={13} style={{marginRight:4,verticalAlign:"middle"}}/>Scadenza</div>
            <input type="date" value={task.dueDate||""} onChange={e=>update("dueDate",e.target.value)}
              style={{border:"none",background:"transparent",fontSize:13,color:isOverdue?"#dc2626":"#111827",cursor:"pointer",fontFamily:"'Inter',sans-serif",outline:"none"}}/>
            <input type="time" value={task.dueTime||""} onChange={e=>update("dueTime",e.target.value)}
              style={{border:"1px solid #e5e7eb",borderRadius:6,background:"transparent",fontSize:12,color:task.dueTime?"#374151":"#9ca3af",cursor:"pointer",fontFamily:"'Inter',sans-serif",outline:"none",padding:"2px 6px"}}/>
          </div>

          {/* Priorità */}
          <div className="detail-field">
            <div className="detail-field-label"><Flag size={13} style={{marginRight:4,verticalAlign:"middle"}}/>Priorità</div>
            <select value={task.priority||"media"} onChange={e=>update("priority",e.target.value)}
              style={{border:"none",background:"transparent",fontSize:13,cursor:"pointer",fontFamily:"'Inter',sans-serif",outline:"none",color:task.priority==="alta"?"#dc2626":task.priority==="bassa"?"#059669":"#d97706"}}>
              <option value="alta">Alta</option>
              <option value="media">Media</option>
              <option value="bassa">Bassa</option>
            </select>
          </div>

          {/* Promemoria */}
          <div className="detail-field">
            <div className="detail-field-label">Avviso giorni prima</div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <input type="checkbox" checked={!!task.reminderEnabled} onChange={e=>update("reminderEnabled",e.target.checked)} style={{accentColor:"#7c3aed"}}/>
              {task.reminderEnabled && <>
                <input type="number" value={task.reminderDays||1} onChange={e=>update("reminderDays",parseInt(e.target.value)||1)}
                  min={1} max={365} style={{width:50,border:"1px solid #e5e7eb",borderRadius:4,padding:"2px 6px",fontSize:12,fontFamily:"'Inter',sans-serif"}}/>
                <span style={{fontSize:12,color:"#6b7280"}}>giorni prima</span>
              </>}
            </div>
          </div>

          {/* Email scadenza */}
          <div className="detail-field">
            <div className="detail-field-label">📧 Email scadenza</div>
            <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13}}>
              <input type="checkbox" checked={task.emailOnDue !== false} onChange={e=>update("emailOnDue",e.target.checked)} style={{accentColor:"#7c3aed"}}/>
              Invia mail alla scadenza
            </label>
          </div>

          {/* Email promemoria */}
          <div className="detail-field">
            <div className="detail-field-label">📧 Email promemoria</div>
            <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13}}>
              <input type="checkbox" checked={task.emailOnReminder !== false} onChange={e=>update("emailOnReminder",e.target.checked)} style={{accentColor:"#7c3aed"}}/>
              Invia mail al promemoria
            </label>
          </div>

          {/* Email scaduta */}
          <div className="detail-field">
            <div className="detail-field-label">📧 Email scaduta</div>
            <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:13}}>
              <input type="checkbox" checked={task.emailOnOverdue !== false} onChange={e=>update("emailOnOverdue",e.target.checked)} style={{accentColor:"#7c3aed"}}/>
              Invia mail se scaduta
            </label>
          </div>

          {/* Ricorrenza */}
          <div className="detail-field">
            <div className="detail-field-label">Ricorrenza</div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <select value={task.recurrence||"nessuna"} onChange={e=>update("recurrence",e.target.value)}
                style={{border:"none",background:"transparent",fontSize:13,cursor:"pointer",fontFamily:"'Inter',sans-serif",outline:"none"}}>
                <option value="nessuna">Nessuna</option>
                <option value="personalizzata">Personalizzata</option>
              </select>
              {task.recurrence==="personalizzata" && <>
                <input type="number" value={task.recurrenceCustomValue||1} onChange={e=>update("recurrenceCustomValue",parseInt(e.target.value)||1)}
                  min={1} style={{width:50,border:"1px solid #e5e7eb",borderRadius:4,padding:"2px 6px",fontSize:12,fontFamily:"'Inter',sans-serif"}}/>
                <select value={task.recurrenceCustomUnit||"settimane"} onChange={e=>update("recurrenceCustomUnit",e.target.value)}
                  style={{border:"none",background:"transparent",fontSize:12,cursor:"pointer",fontFamily:"'Inter',sans-serif",outline:"none"}}>
                  {["giorni","settimane","mesi","anni"].map(u=><option key={u} value={u}>{u}</option>)}
                </select>
              </>}
            </div>
          </div>

          {/* Tag */}
          <div className="detail-field" style={{alignItems:"flex-start",flexDirection:"column"}}>
            <div className="detail-field-label" style={{marginBottom:6}}><Tag size={13} style={{marginRight:4,verticalAlign:"middle"}}/>Tag</div>
            <input className="input" defaultValue={task.tags?.join(", ")||""}
              onBlur={e=>update("tags",e.target.value.split(",").map(t=>t.trim()).filter(Boolean))}
              placeholder="es. urgente, cliente" style={{fontSize:13}}/>
          </div>
          </fieldset>
          {/* Promemoria a orario scelto: personale, si imposta anche su un'attività di sola consultazione */}
          <div className="detail-field">
            <div className="detail-field-label">🔔 Ricordamelo</div>
            <RicordamiAlle collezione="tasks" item={task} projectId={task.projectId || null} />
          </div>
        </div>

        {/* Descrizione */}
        <div className="detail-section">
          <div className="detail-section-title"><AlignLeft size={12} style={{marginRight:4}}/>Descrizione</div>
          <DescrizioneAttivita key={task.id} valore={task.description||""} onSalva={v=>update("description",v)} disabilitato={bloccato}/>
        </div>

        {/* Campi dati */}
        <CustomFieldsSection task={task} sectionId={task.sectionId} projectId={task.projectId} commessaChiusa={commessaChiusa}/>

        {/* Sottoattività */}
        <SubtasksSection taskId={task.id} taskTitle={task.title} organizationId={task.organizationId} projectId={task.projectId || null} onSelectSubtask={onSelectSubtask} selectedSubtaskId={selectedSubtaskId} commessaChiusa={commessaChiusa} ruoloAttivita={ruolo}/>

        {/* Commenti */}
        <CommentiAttivita taskId={task.id} task={task} orgId={task.organizationId} projectId={task.projectId || null} commessaChiusa={commessaChiusa}/>
      </div>
    </div>
  );
}

function AssigneeField({ task, onUpdate, disabled=false }) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  // FIX 13/09: mancava il filtro per organizzazione — la query leggeva l'intera collezione
  // "users" filtrando solo per enabled, senza limitarla all'organizzazione di questo task.
  // Per il Platform Owner passava comunque (bypassa le Regole), quindi il bug è rimasto
  // invisibile finché non abbiamo testato con un account titolare normale: per chiunque
  // non sia Platform Owner le Regole rifiutano una lettura così ampia, quindi la lista
  // arrivava sempre vuota — "Assegnato a" mostrava solo "Nessuno", mai un nome, nemmeno il
  // proprio.
  useEffect(() => {
    if (!task?.organizationId) return;
    getDocs(query(collection(db,"users"),where("organizationId","==",task.organizationId),where("enabled","==",true))).then(s=>setUsers(s.docs.map(d=>d.data())));
  }, [task?.organizationId]);

  return (
    <div className="detail-field" style={{position:"relative"}}>
      <div className="detail-field-label"><User size={13} style={{marginRight:4,verticalAlign:"middle"}}/>Assegnato a</div>
      <button onClick={()=>{ if(!disabled) setOpen(!open); }} style={{border:"none",background:"transparent",cursor:disabled?"default":"pointer",display:"flex",alignItems:"center",gap:6,fontSize:13,fontFamily:"'Inter',sans-serif",color:task.assignedName?"#111827":"#9ca3af"}}>
        {task.assignedName ? <>
          <div className="avatar" style={{width:20,height:20,fontSize:10}}>{task.assignedName[0]?.toUpperCase()}</div>
          {task.assignedName}
        </> : "Nessuno"}
      </button>
      {open && (
        <div style={{position:"absolute",top:"100%",left:0,background:"#fff",border:"1px solid #e5e7eb",borderRadius:8,boxShadow:"0 8px 24px rgba(0,0,0,0.12)",zIndex:100,minWidth:180,padding:4}}>
          <button onClick={()=>{onUpdate({assignedTo:"",assignedName:""});setOpen(false);}}
            style={{width:"100%",padding:"7px 10px",border:"none",background:"transparent",cursor:"pointer",textAlign:"left",fontSize:13,color:"#6b7280",fontFamily:"'Inter',sans-serif"}}>
            Nessuno
          </button>
          {users.map(u=>(
            <button key={u.uid} onClick={()=>{onUpdate({assignedTo:u.uid,assignedName:u.displayName});setOpen(false);}}
              style={{width:"100%",padding:"7px 10px",border:"none",background:task.assignedTo===u.uid?"#f5f3ff":"transparent",cursor:"pointer",textAlign:"left",fontSize:13,fontFamily:"'Inter',sans-serif",display:"flex",alignItems:"center",gap:8,borderRadius:4}}>
              <div className="avatar" style={{width:22,height:22,fontSize:11}}>{u.displayName[0]?.toUpperCase()}</div>
              {u.displayName}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ── Condivisione dell'attività (ottobre 2026) ───────────────────────────────
// Si condivide con colleghi della stessa azienda. Chi la riceve collabora: commenti,
// sottoattività, nuove attività nella stessa sezione; può smettere di seguirla.
function CondivisioneSection({ task, puoCondividere, collabora, commessaChiusa }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const [utenti, setUtenti] = useState([]);
  const [aperto, setAperto] = useState(false);
  const [scelti, setScelti] = useState([]);
  const [nuova, setNuova] = useState(null); // titolo della nuova attività
  const [creando, setCreando] = useState(false);
  const seguo = (task.condivisaCon || []).includes(currentUser?.uid);
  useEffect(() => {
    if (!task?.organizationId) return;
    getDocs(query(collection(db,"users"),where("organizationId","==",task.organizationId),where("enabled","==",true)))
      .then(s=>setUtenti(utentiCondivisibili(s.docs.map(d=>({uid:d.id,...d.data()})), { mioUid: currentUser?.uid, orgId: task.organizationId }))).catch(()=>{});
  }, [task?.organizationId, currentUser?.uid]);
  const condivisa = task.condivisaCon || [];
  const nomi = task.condivisaConNomi || {};
  const ora = () => new Date().toISOString();

  const salva = async () => {
    const { aggiunti, tolti } = differenzaPersone(condivisa, scelti);
    if (!aggiunti.length && !tolti.length) { setAperto(false); return; }
    const nuoviNomi = { ...nomi };
    aggiunti.forEach(uid => { nuoviNomi[uid] = utenti.find(u=>u.uid===uid)?.displayName || ""; });
    tolti.forEach(uid => { delete nuoviNomi[uid]; });
    const voce = { il: ora(), chi: currentUser?.displayName || "", aggiunti: aggiunti.map(u=>nuoviNomi[u]||u), tolti: tolti.map(u=>nomi[u]||u) };
    try {
      await updateDoc(doc(db,"tasks",task.id), { condivisaCon: scelti, condivisaConNomi: nuoviNomi, storicoCondivisione: arrayUnion(voce), updatedAt: serverTimestamp() });
      await Promise.all(aggiunti.map(uid => addDoc(collection(db,"notifiche"), {
        userId: uid, orgId: task.organizationId, tipo: "attivita_condivisa", titolo: "Attività condivisa con te",
        testo: `${currentUser?.displayName || "Un collega"} ha condiviso con te «${task.title}»`,
        projectId: task.projectId || null, taskId: task.id, letta: false, createdAt: serverTimestamp() })));
      addToast(aggiunti.length ? "Attività condivisa: le persone ricevono una notifica" : "Condivisione aggiornata", "success");
      setAperto(false);
    } catch (e) { addToast("Condivisione non salvata", "error"); }
  };
  const nonSeguire = async () => {
    const n = { ...nomi }; delete n[currentUser.uid];
    await updateDoc(doc(db,"tasks",task.id), { condivisaCon: arrayRemove(currentUser.uid), condivisaConNomi: n,
      storicoCondivisione: arrayUnion({ il: ora(), chi: currentUser?.displayName || "", azione: "ha smesso di seguirla" }), updatedAt: serverTimestamp() });
    addToast("Non segui più questa attività", "info");
  };
  const creaAttivita = async () => {
    if (creando || !nuova?.trim()) return;
    if (!task.sectionId) { addToast("Questa attività non è in una sezione: la nuova attività va creata dalla commessa", "error"); return; }
    setCreando(true);
    try {
      // nuova attività nella stessa sezione: la vedono anche chi ha creato e chi ha in carico quella di partenza
      const con = [...new Set([task.createdBy, task.assignedTo].filter(u => u && u !== currentUser.uid))];
      const conNomi = {}; con.forEach(uid => { conNomi[uid] = utenti.find(u=>u.uid===uid)?.displayName || (uid===task.assignedTo ? task.assignedName : "") || ""; });
      const nuovaRef = await addDoc(collection(db,"tasks"), {
        title: nuova.trim(), sectionId: task.sectionId, projectId: task.projectId || "", organizationId: task.organizationId,
        status: "aperta", priority: "media", assignedTo: "", assignedName: "", dueDate: "", description: `Nata da «${task.title}»`,
        tags: [], reminderEnabled: false, reminderDays: 1, recurrence: "nessuna", customFields: {},
        condivisaCon: con, condivisaConNomi: conNomi,
        createdBy: currentUser.uid, createdByName: currentUser.displayName || "", createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      await Promise.all(con.map(uid => addDoc(collection(db,"notifiche"), { userId: uid, orgId: task.organizationId, tipo: "attivita_condivisa",
        titolo: "Nuova attività", testo: `${currentUser?.displayName || "Un collega"} ha aggiunto «${nuova.trim()}» partendo da «${task.title}»`,
        projectId: task.projectId || null, taskId: nuovaRef.id, letta: false, createdAt: serverTimestamp() }))).catch(()=>{});
      addToast("Attività aggiunta nella stessa sezione", "success"); setNuova(null);
    } catch (e) { addToast(messaggioErrore(e, "creare l'attività"), "error"); }
    finally { setCreando(false); }
  };

  return (
    <div className="detail-field" style={{alignItems:"flex-start",flexDirection:"column",gap:6}}>
      <div style={{display:"flex",alignItems:"center",gap:8,width:"100%"}}>
        <div className="detail-field-label">👥 Condivisa con</div>
        <div style={{display:"flex",gap:4,flexWrap:"wrap",flex:1}}>
          {condivisa.length===0 && <span style={{fontSize:12,color:"#9ca3af"}}>nessuno</span>}
          {condivisa.map(uid => (
            <span key={uid} title={nomi[uid]||""} style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:11.5,background:"#f5f3ff",color:"#5b21b6",borderRadius:12,padding:"2px 8px"}}>
              <span className="avatar" style={{width:16,height:16,fontSize:9}}>{(nomi[uid]||"?")[0]?.toUpperCase()}</span>{nomi[uid]||"collega"}
            </span>
          ))}
        </div>
        {puoCondividere && <button className="btn btn-ghost btn-sm" style={{fontSize:12}} onClick={()=>{ setScelti(condivisa); setAperto(a=>!a); }}>Condividi</button>}
        {collabora && seguo && <button className="btn btn-ghost btn-sm" style={{fontSize:12}} onClick={nonSeguire}>Non seguire più</button>}
      </div>
      {collabora && (
        <div style={{fontSize:11.5,color:"#6b7280",background:"#f9fafb",borderRadius:8,padding:"6px 10px",width:"100%",boxSizing:"border-box"}}>
          {seguo ? "Questa attività è condivisa con te: ricevi gli avvisi e puoi aggiungere nuove attività nella stessa sezione." : "Puoi aggiungere nuove attività nella stessa sezione."}
          {!commessaChiusa && (nuova===null
            ? <div><button onClick={()=>setNuova("")} style={{marginTop:4,border:"none",background:"none",color:"#7c3aed",fontWeight:600,cursor:"pointer",padding:0,fontSize:12}}>+ Aggiungi un'attività nella stessa sezione</button></div>
            : <div style={{display:"flex",gap:6,marginTop:6}}>
                <input className="input" autoFocus value={nuova} onChange={e=>setNuova(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter") creaAttivita(); }} placeholder="Titolo della nuova attività" style={{flex:1,fontSize:13}}/>
                <button className="btn btn-primary btn-sm" onClick={creaAttivita} disabled={creando}>{creando ? "..." : "Aggiungi"}</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>setNuova(null)}>Annulla</button>
              </div>)}
        </div>
      )}
      {aperto && (
        <div style={{width:"100%",border:"1px solid #e5e7eb",borderRadius:8,padding:8,background:"#fff",maxHeight:240,overflowY:"auto",boxSizing:"border-box"}}>
          <div style={{fontSize:11,color:"#6b7280",marginBottom:6}}>Colleghi della tua azienda — riceveranno una notifica e potranno collaborare (commenti, sottoattività, nuove attività).</div>
          {utenti.map(u => (
            <label key={u.uid} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 2px",fontSize:13,cursor:"pointer"}}>
              <input type="checkbox" checked={scelti.includes(u.uid)} onChange={e=>setScelti(sc=>e.target.checked?[...sc,u.uid]:sc.filter(x=>x!==u.uid))} style={{accentColor:"#7c3aed"}}/>
              <span className="avatar" style={{width:20,height:20,fontSize:10}}>{(u.displayName||"?")[0]?.toUpperCase()}</span>{u.displayName||u.email}
            </label>
          ))}
          <div style={{display:"flex",gap:6,marginTop:6}}>
            <button className="btn btn-primary btn-sm" onClick={salva}>Salva</button>
            <button className="btn btn-ghost btn-sm" onClick={()=>setAperto(false)}>Annulla</button>
          </div>
        </div>
      )}
    </div>
  );
}
